import type { CountryCode } from "libphonenumber-js";

import { InternationalMdnInput } from "@expert/common-ui";
import { Stack, Text } from "@mantine/core";

import { MdnInput } from "@/common-ui";
import { type Task, useCallbacksRemaining, useSession } from "@/sdk";

import type { CallbackFormVariant, CallbackSurface } from "../../types";

import { useCallbacksFormStore } from "../../callbacksForm.store";
import { CallbackActions } from "./CallbackActions";
import { CallbackTypeSelection } from "./CallbackTypeSelection";
import { useScheduleCallbackForm } from "./useScheduleCallbackForm";

// TODO: remove onBackClick & afterSubmitClickDefault & reachedCallbackCutoff when DeprecatedSessionSummary is deleted.
interface ScheduleCallbackFormProps {
    callbackSurface: CallbackSurface;
    onBackClick?: () => void;
    afterSubmitClickDefault?: () => void;
    shouldShowActions?: boolean;
    reachedCallbackCutoff?: boolean;
    isInternationalPhoneEnabled?: boolean;
    defaultCountry?: CountryCode;
}

function getFormVariant(activeTask: Task | undefined) {
    if (!activeTask) {
        return "post-call";
    }

    return activeTask.status === "wrapping" ? "post-call" : "active-call";
}

function applyScheduleCallbackDefaults(variant: CallbackFormVariant) {
    const { setCallbackType, setSelectedCallbackNowDelay } = useCallbacksFormStore.getState();
    switch (variant) {
        case "active-call":
            setCallbackType("callback-user-with-delay");
            break;
        case "post-call":
            setCallbackType("callback-user-now");
            setSelectedCallbackNowDelay(0);
            break;
    }
}

export function ScheduleCallbackForm({
    callbackSurface,
    onBackClick,
    afterSubmitClickDefault,
    shouldShowActions = true,
    reachedCallbackCutoff,
    isInternationalPhoneEnabled,
    defaultCountry,
}: ScheduleCallbackFormProps) {
    const session = useSession();
    const activeTask = session.currentTask;
    const variant: CallbackFormVariant = getFormVariant(activeTask);
    const { callbacksRemaining } = useCallbacksRemaining();

    const {
        selectedCallbackMdn,
        selectedCallbackOption,
        defaultMdn,
        isSubmitting,
        isCallbackMdnValid,
        onSubmit,
        onMdnInput,
    } = useScheduleCallbackForm({
        callbackSurface,
        afterSubmitClickDefault,
        defaultCountry,
        applyDefaults: () => applyScheduleCallbackDefaults(variant),
    });

    const props = {
        value: selectedCallbackMdn,
        onInput: onMdnInput,
        autoFocus: true,
        maw: "30rem",
        mb: "xs",
        size: "sm",
        defaultCountry,
        defaultValue: defaultMdn,
    };

    return (
        <Stack>
            <Stack gap="xs" mb="xl">
                {callbacksRemaining <= 1 && (
                    <Text size="xs" c={"warning.5"}>
                        {callbacksRemaining} callback remaining
                    </Text>
                )}
                <Text size="xs">Callback number</Text>
                {isInternationalPhoneEnabled ? <InternationalMdnInput {...props} /> : <MdnInput {...props} />}
                <Text size="xs">Callback time</Text>
                <CallbackTypeSelection value={selectedCallbackOption} variant={variant} />
            </Stack>
            {shouldShowActions && (
                <CallbackActions
                    onSubmit={onSubmit}
                    onBackClick={onBackClick}
                    formVariant={variant}
                    isSubmitting={isSubmitting}
                    reachedCallbackCutoff={reachedCallbackCutoff}
                    isCallbackMdnValid={isCallbackMdnValid}
                    data-testid="call-controls-callback-schedule-complete"
                />
            )}
        </Stack>
    );
}
